/**
 * Scan Station folder watcher (86d3rrfbf) — File System Access API, Chromium only.
 *
 * The scanner drops PDFs into a folder; this watches that folder and feeds each finished file
 * into the same intake endpoint the manual drop zone uses.
 *
 * ## Two independent jobs, deliberately not one loop
 *
 * NOTICING a file and UPLOADING it run at completely different rates, so they are separate:
 *
 * - a **sweep** (every {@link POLL_MS}) walks the folder, decides which files have finished being
 *   written, and pushes those onto a ready queue;
 * - a **worker pool** ({@link UPLOAD_CONCURRENCY} slots) drains that queue continuously, without
 *   waiting for a sweep.
 *
 * They must stay independent in BOTH directions. Uploading inside the sweep serialises the whole
 * station behind one request; but "enumerate once, then drain the batch" is just as wrong — during a
 * multi-minute drain nothing would notice a newly-dropped file, which is the ordinary single-scan
 * case this station exists for.
 *
 * ## Bounded work per sweep
 *
 * Users paste thousands of PDFs in at once for backfills. Iterating directory ENTRY NAMES is cheap;
 * `getFile()` is the expensive call (a round trip to the browser's file-system process, then to the
 * OS — and to a network share or an antivirus filter driver, if that is what the folder is). The old
 * sweep called `getFile()` on EVERY pdf and uploaded exactly one, so moving N files cost ~N²/2 of
 * them: about 4.5 million for 3,000 files. Now a sweep skips every file that cannot change state
 * right now and probes at most {@link MAX_PROBES_PER_TICK} of the rest, least-recently-probed first,
 * so the cost is ~4 `getFile()` calls per file no matter how big the folder is.
 *
 * ## Still conservative about the things that matter
 *
 * - **Never uploads a file that is still being written.** A scanner writes a multi-page PDF
 *   progressively, so a file is only "stable" once its size has been unchanged across repeated
 *   sightings AND it has been quiet for {@link QUIET_MS}. Uploading early means a truncated,
 *   undecodable PDF. Bounded probing makes sightings *further* apart, never closer, and a file is
 *   probed at most once per sweep — so the sightings behind a stability decision are always
 *   separated by real time.
 * - **Moves on success AND on duplicate**, into `uploaded/`. A duplicate means the bytes are
 *   already safely stored, so leaving the file behind would retry it forever.
 * - **Survives a reload** by persisting the directory handle in IndexedDB; the browser still
 *   requires a user gesture to re-grant, which is the one-click Resume.
 */

import { documentCaptureService } from '../services/documentCaptureService';

const DB_NAME = 'dop-document-capture';
const STORE_NAME = 'handles';
const HANDLE_KEY = 'scan-folder';
const UPLOADED_DIR = 'uploaded';

/** How often a sweep starts. Exported so tests drive whole ticks instead of copying the number. */
export const POLL_MS = 2000;
/** A file must also be untouched for this long — two equal polls alone can hit a slow writer's pause. */
const QUIET_MS = 5000;
const HEARTBEAT_MS = 60000;
/** Stop re-uploading a file after this many consecutive failures — a persistently-failing file must not
 * re-upload every poll forever (it hammers the API and spams the journey). It stays on disk for a human. */
export const MAX_UPLOAD_ATTEMPTS = 3;

/**
 * Most `getFile()` calls one sweep may make. Everything else in a sweep (iterating entry names,
 * checking the `seen` map) is cheap; this is the only line item that scales with folder size.
 *
 * 300 because a sweep has to finish comfortably inside {@link POLL_MS}. `getFile()` costs roughly
 * 1–5 ms depending on the drive, antivirus and whether the folder is a network share, so 300 is
 * ~0.3–1.5 s of a 2 s budget with room for a slow day. It is also plenty: at 300 probes a tick a
 * 3,000-file paste is fully sighted in 10 ticks and fully queued in ~30 (about a minute), which is
 * an order of magnitude faster than 3,000 uploads take — so the probe cap is never the bottleneck,
 * it just stops the sweep from monopolising the tab.
 */
export const MAX_PROBES_PER_TICK = 300;

/**
 * Uploads allowed in flight at once.
 *
 * 4 because upload latency, not bandwidth, is what limits a station on a WAN/VPN link: one request
 * at a time leaves the pipe idle between round trips. The ceiling is the intake endpoint, which
 * hashes the bytes, writes to S3 and runs a dedupe lookup PER REQUEST — past ~5 concurrent that
 * starts to matter. Browsers also allow ~6 connections per origin and the live SSE stream holds one
 * of them, so 4 leaves headroom for the rest of the page. Do not raise this above 5.
 */
export const UPLOAD_CONCURRENCY = 4;

export type WatcherStatus = 'watching' | 'nofolder' | 'resume' | 'unsupported' | 'lost';

/**
 * How much work the station is holding. Reported on every meaningful change so a multi-hour import
 * can say something truer than "Watching" — an operator who pastes 3,000 files and sees no number
 * has no way to tell progress from a hang.
 */
export interface WatcherProgress {
  /** Stable files sitting on the ready queue, waiting for a free upload slot. */
  waiting: number;
  /** Uploads in flight right now. Never more than {@link UPLOAD_CONCURRENCY}. */
  uploading: number;
  /**
   * PDFs the last sweep saw in the folder that are not queued, uploading or given up on — i.e. still
   * inside the stability window, or simply not probed yet this round. Exact as of the last sweep.
   */
  pending: number;
  /**
   * A sweep is walking the folder AND there is unfinished work, so the numbers above may still grow.
   * Deliberately false during an idle sweep over a quiet folder: that is not "discovering", and
   * flipping a flag every 2 s forever would be noise, not information.
   */
  discovering: boolean;
}

export interface WatcherCallbacks {
  onStatus: (status: WatcherStatus, folderName: string | null) => void;
  /** Fired the moment a stable file is picked up, before the upload — the row must appear now. */
  onFileStart: (key: string, fileName: string) => void;
  onFileDone: (
    key: string,
    result: {
      docNumber: string | null;
      duplicate: boolean;
      fileId: number;
      // The existing document's state on a duplicate (86d3uf18j L2) — null for treasury docs the
      // caller can't see. Lets the journey row show more than a bare "Already captured".
      docStatus: number | null;
      linkStatus: number | null;
    }
  ) => void;
  onFileError: (key: string, message: string) => void;
  /** Queue depth for the UI. Called only when the numbers actually change, never on every tick. */
  onProgress: (progress: WatcherProgress) => void;
}

/** Chromium-only API; Firefox/Safari fall back to the manual drop zone. */
export function isFileSystemAccessSupported(): boolean {
  return typeof window !== 'undefined' && 'showDirectoryPicker' in window;
}


// ---------------------------------------------------------------- IndexedDB handle persistence

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE_NAME);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function idbSet(value: FileSystemDirectoryHandle | null): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    if (value) tx.objectStore(STORE_NAME).put(value, HANDLE_KEY);
    else tx.objectStore(STORE_NAME).delete(HANDLE_KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

async function idbGet(): Promise<FileSystemDirectoryHandle | null> {
  const db = await openDb();
  const value = await new Promise<FileSystemDirectoryHandle | null>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(HANDLE_KEY);
    req.onsuccess = () => resolve((req.result as FileSystemDirectoryHandle) ?? null);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return value;
}

// ---------------------------------------------------------------- watcher

interface SeenFile {
  size: number;
  /** When the size was last observed to CHANGE — the quiet window is measured from here. */
  lastChangedAt: number;
  polls: number;
  /**
   * Sweep number of the last `getFile()` probe; 0 = never probed. This is the round-robin cursor:
   * a sweep probes the candidates with the SMALLEST value first (see {@link ScanWatcher.poll}).
   * A sweep counter rather than a timestamp because `Date.now()` ties across a whole sweep, and
   * ties would let the same head-of-list files win every round and starve the tail.
   */
  lastProbedSweep: number;
  /** On the ready queue awaiting an upload slot. Probing it again could only re-queue it. */
  queued?: boolean;
  uploading?: boolean;
  /** Consecutive failed upload attempts; at {@link MAX_UPLOAD_ATTEMPTS} the file is given up on. */
  failures: number;
  /** Set once retries are exhausted — sweeps then skip it so it never re-uploads on a loop. */
  giveUp?: boolean;
  /**
   * Correlation id for this physical file (86d3uf18j) — minted once on first sighting and REUSED
   * across retry attempts, so the server (which echoes it in the live stream) and this browser share
   * one identifier regardless of whether any single upload response comes back. Also the journey-row
   * key, so retries reuse one row instead of stacking duplicates.
   */
  clientUploadId: string;
}

/** A file that has finished being written and is waiting for an upload slot. */
interface ReadyJob {
  name: string;
  file: File;
  /**
   * The directory this file was READ from, captured at ENQUEUE time — deliberately not `this.handle`
   * at upload time. A queued file can wait minutes behind a backlog, and `chooseFolder()` may swap
   * the live handle in that window; archiving through the live handle would then copy-and-DELETE a
   * same-named, never-uploaded file out of the NEW folder. (`chooseFolder()` also drops the queue,
   * so this is belt and braces — but the belt is what keeps a file from being destroyed.)
   */
  dir: FileSystemDirectoryHandle;
}

export class ScanWatcher {
  private handle: FileSystemDirectoryHandle | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private seen = new Map<string, SeenFile>();
  /** Files confirmed stable, in discovery order. Drained by {@link pump}, never by the sweep. */
  private ready: ReadyJob[] = [];
  private inFlight = 0;
  /**
   * A sweep is running. Guards the WHOLE sweep, not just the upload: at 3,000 files a sweep can
   * outlast {@link POLL_MS} (antivirus, network drives), and without this the interval would stack
   * sweeps on top of each other — each one re-probing what the other was already probing.
   */
  private sweeping = false;
  /** Monotonic sweep counter; the round-robin cursor is `SeenFile.lastProbedSweep`. */
  private sweepSeq = 0;
  /** PDFs the last sweep found that still need attention. See {@link WatcherProgress.pending}. */
  private pending = 0;
  /** Signature of the last reported progress, so an unchanged tick reports nothing. */
  private lastProgress = '';
  private filesToday = 0;
  private stationId: string;
  /**
   * Set by {@link dispose}. A disposed watcher can NEVER start, poll or upload again.
   *
   * ⚠️ Load-bearing (fixes the duplicate-row bug). Attaching is async — `tryResumeSilently` awaits
   * IndexedDB and a permission query before calling `start()`. React effect cleanup (and StrictMode's
   * deliberate double-invoke in dev) therefore runs BEFORE that resolves, when there are no timers to
   * clear, so `stop()` alone is a no-op and the in-flight attach later resurrects the discarded
   * instance. Two live watchers then poll the same folder with separate `seen` maps: each mints its own
   * correlation id for the same file and opens its own journey row — two rows, one physical scan.
   */
  private disposed = false;

  constructor(private callbacks: WatcherCallbacks, stationId?: string) {
    this.stationId = stationId ?? deriveStationId();
  }

  /** Re-attach to a previously chosen folder if the browser still grants it. No prompt. */
  async tryResumeSilently(): Promise<void> {
    if (!isFileSystemAccessSupported()) {
      this.callbacks.onStatus('unsupported', null);
      return;
    }
    const stored = await idbGet().catch(() => null);
    if (this.disposed) return;      // cleanup ran while we awaited — never resurrect
    if (!stored) {
      this.callbacks.onStatus('nofolder', null);
      return;
    }
    const permission = await stored.queryPermission({ mode: 'readwrite' }).catch(() => 'denied' as PermissionState);
    if (this.disposed) return;
    if (permission === 'granted') {
      this.handle = stored;
      this.start();
      return;
    }
    // Handle survived but the grant didn't — one click restores it.
    this.handle = stored;
    this.callbacks.onStatus('resume', stored.name);
  }

  /** Re-request permission on a stored handle. MUST be called from a user gesture. */
  async resume(): Promise<void> {
    if (this.disposed) return;
    if (!this.handle) {
      // No stored handle to re-grant — fall through to the picker. Its return value is the caller's
      // business, not ours: resume() promises nothing but "do the right thing".
      await this.chooseFolder();
      return;
    }
    const permission = await this.handle.requestPermission({ mode: 'readwrite' }).catch(() => 'denied' as PermissionState);
    if (this.disposed) return;
    if (permission === 'granted') this.start();
    else this.callbacks.onStatus('resume', this.handle.name);
  }

  /**
   * Folder picker. MUST be called from a user gesture.
   *
   * Returns the name of the folder now being watched, or `null` when the picker was dismissed or the
   * API is unsupported — dismissal changes nothing, so the caller can confirm a real switch without
   * also announcing a cancel. (Do NOT confirm from `onStatus('watching')` instead: that also fires on
   * the silent resume at page load, which the operator did not ask for.)
   */
  async chooseFolder(): Promise<string | null> {
    if (!isFileSystemAccessSupported()) {
      this.callbacks.onStatus('unsupported', null);
      return null;
    }
    if (this.disposed) return null;
    try {
      const handle = await window.showDirectoryPicker({ mode: 'readwrite', id: 'dop-scan-folder' });
      if (this.disposed) return null;
      this.handle = handle;
      // A different folder means a different set of files, and `seen` is keyed by NAME alone — so a
      // leftover entry would (a) suppress a same-named file here forever if the old one was given up
      // on, (b) skip the quiet window through a stale `lastChangedAt`, and (c) hand the new file the
      // old one's correlation id, merging two physical scans into one journey row.
      //
      // Cleared on EVERY successful pick, including re-picking the SAME folder after a drop. That is
      // deliberate: it hands previously given-up files a fresh attempt budget, which doubles as the
      // manual retry the station otherwise has no affordance for.
      this.seen.clear();
      // The ready queue goes with it: those jobs belong to the OLD folder, and letting them drain
      // after a switch would archive old-folder files while the operator watches a new one.
      // (Uploads already IN FLIGHT keep their own captured handle and finish honestly.)
      this.dropQueue();
      await idbSet(handle).catch(() => undefined);
      if (this.disposed) return null;
      this.start();
      return handle.name;
    } catch {
      // The user dismissed the picker — not an error, just no change.
      if (!this.handle) this.callbacks.onStatus('nofolder', null);
      return null;
    }
  }

  async disconnect(): Promise<void> {
    this.stop();
    this.handle = null;
    this.seen.clear();
    this.dropQueue();
    await idbSet(null).catch(() => undefined);
    this.callbacks.onStatus('nofolder', null);
  }

  private start(): void {
    if (this.disposed) return;
    this.stop();
    this.callbacks.onStatus('watching', this.handle?.name ?? null);
    this.timer = setInterval(() => void this.poll(), POLL_MS);
    this.heartbeatTimer = setInterval(() => void this.sendHeartbeat(), HEARTBEAT_MS);
    void this.sendHeartbeat();
    void this.poll();
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.timer = null;
    this.heartbeatTimer = null;
  }

  /**
   * Retire this instance for good — what React effect cleanup must call. Unlike {@link stop} this is
   * irreversible, so an attach that is still awaiting cannot bring the watcher back and start a second
   * poll loop over the same folder (see {@link disposed}).
   */
  dispose(): void {
    this.disposed = true;
    this.stop();
    this.ready = [];
  }

  private async sendHeartbeat(): Promise<void> {
    try {
      await documentCaptureService.heartbeat(this.stationId, this.filesToday);
    } catch {
      // Heartbeat is telemetry for the health tile; never let it disturb capture.
    }
  }

  // ---------------------------------------------------------------- noticing (the sweep)

  /**
   * One sweep: notice files, decide which have finished being written, hand those to the workers.
   * Uploads are NOT done here — {@link pump} owns those and runs on its own.
   *
   * Two passes, because they cost wildly different amounts:
   *
   * 1. Iterate every entry NAME (cheap) and collect the ones that could still change state. A file
   *    that is queued, uploading or given up on cannot, so it is skipped before it costs anything —
   *    which is what stops a 3,000-file backlog from re-statting itself every 2 seconds.
   * 2. `getFile()` at most {@link MAX_PROBES_PER_TICK} of those candidates (expensive).
   *
   * **Round-robin / anti-starvation.** Pass 2 orders candidates by `lastProbedSweep` ascending —
   * least-recently-probed first, never-probed (0) first of all — and stamps each probed file with
   * the current sweep number. A file that misses a sweep keeps its old, smaller number while the
   * probed ones move to the back, so it is strictly closer to the front next time: every candidate
   * is probed within `ceil(candidates / MAX_PROBES_PER_TICK)` sweeps and none can starve. ("First
   * 300 of the enumeration, every sweep" would starve the tail forever; a `Date.now()` cursor would
   * too, because a whole sweep's probes tie on the same millisecond.)
   *
   * Ordering by staleness also puts brand-new files at the head of the queue, so a single scan
   * dropped during a big import is still noticed on the very next sweep.
   */
  private async poll(): Promise<void> {
    if (!this.handle || this.sweeping || this.disposed) return;
    const dir = this.handle;
    this.sweeping = true;
    const sweep = ++this.sweepSeq;

    try {
      // ---- pass 1: names only ----
      const candidates: FileSystemFileHandle[] = [];
      try {
        for await (const entry of dir.values()) {
          if (this.disposed) return;
          if (entry.kind !== 'file' || !entry.name.toLowerCase().endsWith('.pdf')) continue;
          const known = this.seen.get(entry.name);
          // Nothing a probe could learn right now would change what happens to this file.
          if (known && (known.queued || known.uploading || known.giveUp)) continue;
          candidates.push(entry as FileSystemFileHandle);
        }
      } catch {
        // The folder went away (unplugged drive, revoked grant, renamed path).
        this.stop();
        // Queued jobs point at a folder that is no longer there; draining them would be a burst of
        // failures against a dead handle. Drop them — but keep `seen`, so a reconnect re-queues each
        // file under the correlation id it already has instead of opening a second journey row for it.
        this.dropQueue();
        this.callbacks.onStatus('lost', dir.name);
        return;
      }

      this.pending = candidates.length;
      this.emitProgress();
      if (candidates.length === 0) return;

      // ---- pass 2: probe a bounded, fair slice ----
      // Decorate-sort-undecorate: read each cursor ONCE rather than twice per comparison. At 3,000
      // candidates that is ~3,000 map lookups instead of ~140,000, every two seconds.
      // `Array.prototype.sort` is stable (ES2019+), so files tied on the cursor — every file on the
      // very first sweep, for instance — keep the folder's own enumeration order.
      const ordered = candidates
        .map((handle) => ({ handle, cursor: this.seen.get(handle.name)?.lastProbedSweep ?? 0 }))
        .sort((a, b) => a.cursor - b.cursor);
      const budget = Math.min(ordered.length, MAX_PROBES_PER_TICK);

      for (let i = 0; i < budget; i++) {
        if (this.disposed) return;
        const fileHandle = ordered[i].handle;
        const name = fileHandle.name;

        let file: File;
        try {
          file = await fileHandle.getFile();
        } catch {
          // This one file vanished or is locked mid-sweep. Not a reason to declare the FOLDER lost —
          // if the drive really went away, pass 1 of the next sweep will throw and say so.
          continue;
        }
        if (this.disposed) return;

        const now = Date.now();
        const previous = this.seen.get(name);

        if (!previous || previous.size !== file.size) {
          // First sighting, or still growing (a re-written file gets a fresh attempt budget) — restart
          // both stability clocks and the failure count. Keep the correlation id across the growing
          // phase so one physical file has one id from first sighting through upload + retries.
          this.seen.set(name, {
            size: file.size,
            lastChangedAt: now,
            polls: 0,
            lastProbedSweep: sweep,
            failures: 0,
            clientUploadId: previous?.clientUploadId ?? crypto.randomUUID(),
          });
          continue;
        }

        // A file is probed at most ONCE per sweep (candidates are distinct entries), and sweeps are
        // POLL_MS apart, so `polls` only ever counts sightings separated by real elapsed time. The
        // QUIET_MS test below is measured from the last size CHANGE, so it independently guarantees
        // the same thing even if a future change ever made sweeps run back to back.
        const polls = previous.polls + 1;
        const stable = polls >= 2 && now - previous.lastChangedAt >= QUIET_MS && file.size > 0;

        if (!stable) {
          this.seen.set(name, { ...previous, polls, lastProbedSweep: sweep });
          continue;
        }

        this.seen.set(name, { ...previous, polls, lastProbedSweep: sweep, queued: true });
        this.ready.push({ name, file, dir });
        this.pending = Math.max(0, this.pending - 1);
        // Start uploading NOW rather than at the end of the sweep — on a 3,000-file folder the sweep's
        // remaining probes would otherwise hold four idle workers for the rest of the tick.
        this.pump();
      }
    } finally {
      this.sweeping = false;
      this.emitProgress();
    }
  }

  // ---------------------------------------------------------------- uploading (the worker pool)

  /**
   * Fill every free upload slot from the ready queue. Cheap and re-entrant: called after each
   * enqueue and after each upload settles, and a no-op when the pool is full or the queue is empty.
   *
   * The recursion is through a `.finally()` (a microtask), so the pool can run for hours without
   * growing the stack.
   */
  private pump(): void {
    if (this.disposed) return;
    while (this.inFlight < UPLOAD_CONCURRENCY && this.ready.length > 0) {
      const job = this.ready.shift()!;
      this.inFlight += 1;
      void this.upload(job).finally(() => {
        this.inFlight -= 1;
        this.emitProgress();
        this.pump();
      });
    }
    this.emitProgress();
  }

  private async upload(job: ReadyJob): Promise<void> {
    if (this.disposed) return;
    const { name, file, dir } = job;
    const existing = this.seen.get(name);
    const attempt = existing?.failures ?? 0;
    if (existing) this.seen.set(name, { ...existing, queued: false, uploading: true });

    // The journey key IS the correlation id (86d3uf18j): stable across retries of this physical file
    // (so one row, never a stack of duplicates) AND the same id the server echoes in the live stream
    // (so events reconcile even if this upload's response never returns).
    const clientUploadId = existing?.clientUploadId ?? crypto.randomUUID();
    const key = clientUploadId;
    this.callbacks.onFileStart(key, name);

    try {
      const result = await documentCaptureService.uploadScan(file, this.stationId, clientUploadId);
      // A duplicate is a SUCCESS for the folder's purposes: the bytes are already stored, so the
      // file must leave the inbox or it will be retried on every sweep forever.
      await this.moveToUploaded(dir, name);
      this.filesToday += 1;
      this.seen.delete(name);
      this.callbacks.onFileDone(key, {
        docNumber: result.docNumber,
        duplicate: result.duplicate,
        fileId: result.fileId,
        docStatus: result.docStatus,
        linkStatus: result.linkStatus,
      });
    } catch (error) {
      const current = this.seen.get(name);
      const failures = attempt + 1;
      if (failures >= MAX_UPLOAD_ATTEMPTS) {
        // Give up: keep the file on disk for a human, stop re-uploading, and surface the failure.
        // If the server had actually succeeded on an earlier attempt (lost response), the live stream
        // already advanced this row via the correlation id and the store's rank-guard stops this
        // 'failed' from downgrading a captured/linked row — so 'failed' only shows a genuine dead end.
        if (current) this.seen.set(name, { ...current, queued: false, uploading: false, failures, giveUp: true });
        this.callbacks.onFileError(key, error instanceof Error ? error.message : 'Upload failed');
      } else if (current) {
        // Transient — retry after the file has proved stable again. Deliberately do NOT report a
        // failure (86d3uf18j): leave the row 'Uploading…', because the server may well have received
        // it and the stream (or the retry) will resolve it. Flashing 'Failed' on a hand-off that
        // actually succeeded is exactly the phantom-row bug this correlation-id design removes.
        this.seen.set(name, {
          ...current,
          queued: false,
          uploading: false,
          polls: 0,
          lastChangedAt: Date.now(),
          failures,
        });
      }
    }
  }

  /**
   * Move by copy-then-delete: the File System Access API has no rename across directories.
   * If the copy fails we do NOT delete, so a file is never lost — worst case it re-uploads and
   * the server answers "duplicate", which is the honest, harmless outcome.
   *
   * `dir` is the directory the file was READ from, deliberately NOT `this.handle` — see
   * {@link ReadyJob.dir}. Touching the live handle after a mid-upload folder swap would archive the
   * wrong physical file.
   */
  private async moveToUploaded(dir: FileSystemDirectoryHandle, name: string): Promise<void> {
    try {
      const source = await dir.getFileHandle(name);
      const file = await source.getFile();
      const uploadedDir = await dir.getDirectoryHandle(UPLOADED_DIR, { create: true });
      const target = await uploadedDir.getFileHandle(uniqueName(name), { create: true });
      const writable = await target.createWritable();
      await writable.write(file);
      await writable.close();
      await dir.removeEntry(name);
    } catch {
      // Keep the original; the duplicate answer makes a retry safe.
    }
  }

  // ---------------------------------------------------------------- progress

  /** Forget everything still waiting, and let those files be re-noticed from scratch. */
  private dropQueue(): void {
    for (const job of this.ready) {
      const entry = this.seen.get(job.name);
      if (entry) this.seen.set(job.name, { ...entry, queued: false });
    }
    this.ready = [];
    this.pending = 0;
    this.emitProgress();
  }

  private emitProgress(): void {
    if (this.disposed) return;
    const waiting = this.ready.length;
    const outstanding = waiting + this.inFlight + this.pending;
    const progress: WatcherProgress = {
      waiting,
      uploading: this.inFlight,
      pending: this.pending,
      discovering: this.sweeping && outstanding > 0,
    };
    const signature = `${progress.waiting}|${progress.uploading}|${progress.pending}|${progress.discovering}`;
    if (signature === this.lastProgress) return;
    this.lastProgress = signature;
    this.callbacks.onProgress(progress);
  }
}

/** Avoids clobbering a same-named file already archived from an earlier session. */
function uniqueName(name: string): string {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  const dot = name.lastIndexOf('.');
  return dot > 0 ? `${name.slice(0, dot)}__${stamp}${name.slice(dot)}` : `${name}__${stamp}`;
}

/**
 * Stable per-browser station id so the health tile can tell stations apart.
 *
 * 86d3yek7d — exported because the capture page now asks the SERVER how many files this station
 * uploaded today, and it must ask about the same station the watcher uploads as. Deriving it a second
 * time in the page is safe (it is a read-through of one localStorage key) and is what keeps the two
 * from drifting apart.
 */
export function deriveStationId(): string {
  const KEY = 'dop-scan-station-id';
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = `station-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(KEY, id);
  }
  return id;
}
